import { Card, Col, Row, Spinner, Alert, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faCalendar, faCoins, faLink } from "@fortawesome/free-solid-svg-icons";
import { usePrecargaEvents } from "../../hooks/usePrecargaEvents";
import {
  formatCurrency,
  getStatusLabel,
} from "../../utils/precargaHelpers";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Link } from "react-router-dom";

const PRECARGA_BASE_URL = "https://gopal-precarga-staging-970334009292.southamerica-east1.run.app/";

export default function PrecargaEventDetail({ eventId, onEdit }) {
  const { event, isLoadingEvent, isErrorEvent } = usePrecargaEvents({ eventId });

  if (isLoadingEvent) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <p>Cargando evento...</p>
      </div>
    );
  }

  if (isErrorEvent || !event) {
    return (
      <Alert variant="danger">
        No se pudo cargar el evento. Por favor, intente nuevamente.
      </Alert>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "No definido";
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy 'a las' HH:mm", {
      locale: es,
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>{event.name}</h2>
          <Link to={`${PRECARGA_BASE_URL}${event.slug}`} className="text-muted mb-0" target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon icon={faLink} className="me-2" />
            /{event.slug}
          </Link>
        </div>
        <div>
          {onEdit && (
            <Button variant="primary" onClick={() => onEdit(event)}>
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              Editar
            </Button>
          )}
        </div>
      </div>

      <Row className="g-3">
        {/* General Info */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <strong>Información General</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">
                  ID Evento:
                </Col>
                <Col xs={7}>#{event.id}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">
                  ID Cashless:
                </Col>
                <Col xs={7}>#{event.cashlessEventId}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">
                  Estado:
                </Col>
                <Col xs={7}>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: event.status === 'ACTIVE' ? '#198754' : '#212529',
                      color: '#fff'
                    }}
                  >
                    {getStatusLabel(event.status)}
                  </span>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">
                  Moneda:
                </Col>
                <Col xs={7}>{event.currency}</Col>
              </Row>
              {event.disabledReason && (
                <Row className="mb-2">
                  <Col xs={5} className="text-muted">
                    Razón:
                  </Col>
                  <Col xs={7}>
                    <Alert variant="warning" className="mb-0 py-2">
                      {event.disabledReason}
                    </Alert>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Limits */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <FontAwesomeIcon icon={faCoins} className="me-2" />
              <strong>Límites de Recarga</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">
                  Monto Mínimo:
                </Col>
                <Col xs={7}>
                  <strong>{formatCurrency(event.minTopup, event.currency)}</strong>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">
                  Monto Máximo:
                </Col>
                <Col xs={7}>
                  <strong>{formatCurrency(event.maxTopup, event.currency)}</strong>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Preload Period */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <FontAwesomeIcon icon={faCalendar} className="me-2" />
              <strong>Periodo de Precargas</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col xs={4} className="text-muted">
                  Apertura:
                </Col>
                <Col xs={8}>{formatDate(event.preloadOpenAt)}</Col>
              </Row>
              <Row>
                <Col xs={4} className="text-muted">
                  Cierre:
                </Col>
                <Col xs={8}>{formatDate(event.preloadCloseAt)}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Refund Period */}
        <Col md={6}>
          <Card>
            <Card.Header>
              <FontAwesomeIcon icon={faCalendar} className="me-2" />
              <strong>Periodo de Devoluciones</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col xs={4} className="text-muted">
                  Apertura:
                </Col>
                <Col xs={8}>{formatDate(event.refundOpenAt)}</Col>
              </Row>
              <Row>
                <Col xs={4} className="text-muted">
                  Cierre:
                </Col>
                <Col xs={8}>{formatDate(event.refundCloseAt)}</Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Metadata */}
        <Col md={12}>
          <Card>
            <Card.Header>
              <strong>Metadatos</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col xs={2} className="text-muted">
                  Creado:
                </Col>
                <Col xs={10}>
                  {format(new Date(event.createdAt), "dd/MM/yyyy HH:mm:ss", {
                    locale: es,
                  })}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={2} className="text-muted">
                  Actualizado:
                </Col>
                <Col xs={10}>
                  {format(new Date(event.updatedAt), "dd/MM/yyyy HH:mm:ss", {
                    locale: es,
                  })}
                </Col>
              </Row>
              {event.disabledAt && (
                <Row>
                  <Col xs={2} className="text-muted">
                    Deshabilitado:
                  </Col>
                  <Col xs={10}>
                    {format(new Date(event.disabledAt), "dd/MM/yyyy HH:mm:ss", {
                      locale: es,
                    })}
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
